import React, { useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useAuthenticate, Facility } from "../../../lib/api";
import { useInstanceFacilities } from "../../../lib/api-central-mu";

/**
 * FacilitySelector component
 *
 * Dropdown for selecting a facility from the client's available facilities.
 *
 * Central-MU: when source serviceId is set (or target serviceId for isTarget=true), routes
 * through MU's /api/instances/{id}/facilities (LeucineInstanceClient → streem-backend's
 * two-step auth). Falls back to the legacy direct-browser useAuthenticate(client) call when
 * no serviceId is set so legacy flows keep working.
 */
interface FacilitySelectorProps {
  className?: string;
  isTarget?: boolean;
  label?: string;
  client?: any;
}

const FacilitySelector: React.FC<FacilitySelectorProps> = ({
  className,
  isTarget = false,
  label = "Facility",
  client
}) => {
  const {
    selectedFacilityId,
    setSelectedFacilityId,
    clearSelectedChecklists,
    selectedClient,
    selectedServiceId,
    targetFacilityId,
    setTargetFacilityId,
    targetClient,
    targetServiceId,
  } = useWorkflowStore();

  // Use the appropriate client and facility ID based on isTarget
  const currentClient = client || (isTarget ? targetClient : selectedClient);
  const currentFacilityId = isTarget ? targetFacilityId : selectedFacilityId;
  const setFacilityId = isTarget ? setTargetFacilityId : setSelectedFacilityId;

  // Central-MU path: when the relevant serviceId is set, fetch through MU.
  const serviceIdStr = isTarget ? targetServiceId : selectedServiceId;
  const serviceIdNumber = serviceIdStr ? Number(serviceIdStr) : null;
  const useCentralMU = !!serviceIdNumber;

  const centralMUQuery = useInstanceFacilities(useCentralMU ? serviceIdNumber : null);
  const legacyQuery = useAuthenticate(useCentralMU ? null : currentClient);

  const facilities: Facility[] = useCentralMU
    ? ((centralMUQuery.data as unknown as Facility[]) || [])
    : (legacyQuery.data?.facilities || []);
  const isLoading = useCentralMU ? centralMUQuery.isLoading : legacyQuery.isLoading;

  // Reset facility selection when client / instance changes
  useEffect(() => {
    setFacilityId(null);
    if (!isTarget) {
      clearSelectedChecklists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClient, serviceIdNumber]);

  // Handle facility selection change
  const handleFacilityChange = (facilityId: string) => {
    if (!isTarget && facilityId !== currentFacilityId) {
      clearSelectedChecklists();
    }
    setFacilityId(facilityId);
  };

  const hasSource = useCentralMU || !!currentClient;

  return (
    <div className={className}>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor={isTarget ? "target-facility" : "facility"}>{label}</Label>
        <Select
          value={currentFacilityId || undefined}
          onValueChange={handleFacilityChange}
          disabled={isLoading || !hasSource}
        >
          <SelectTrigger id={isTarget ? "target-facility" : "facility"} className="w-full">
            <SelectValue placeholder={isTarget ? "Select facility" : "Select source facility"} />
          </SelectTrigger>
          <SelectContent>
            {isLoading ? (
              <SelectItem value="loading" disabled>
                Loading facilities...
              </SelectItem>
            ) : !hasSource ? (
              <SelectItem value="no-source" disabled>
                Select a {isTarget ? "target" : "source"} instance first
              </SelectItem>
            ) : facilities.length === 0 ? (
              <SelectItem value="no-facilities" disabled>
                No facilities available
              </SelectItem>
            ) : (
              facilities.map((facility) => (
                <SelectItem key={facility.id} value={facility.id}>
                  {facility.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default FacilitySelector;
